import { graphql } from '@/shared/graphql/generated'

/**
 * Datenschicht des Chats zwischen der Frau und ihrer Hebamme (Tabelle `chat_messages`,
 * angelegt mit scripts/add-chat-messages.mjs).
 *
 * Nachrichten kommen als Subscription über den WebSocket von Hasura, damit eine Antwort
 * ohne Neuladen erscheint. Die Mutationen geben deshalb nur die `id` zurück: Die neue
 * Zeile liefert die laufende Subscription selbst.
 *
 * Welches Gespräch eine Nachricht trifft, legt ein Trigger in der Datenbank fest. Die Frau
 * schickt nur den Text, die Hebamme zusätzlich die Frau; Absenderin und Hebamme setzt der
 * Server aus dem Token und der Verbindung in user_profiles.
 */

export const CHAT_MESSAGES = graphql(`
  subscription ChatMessages($patientUserId: uuid!, $midwifeUserId: uuid!) {
    chat_messages(
      where: { patient_user_id: { _eq: $patientUserId }, midwife_user_id: { _eq: $midwifeUserId } }
      order_by: { created_at: desc }
      limit: 300
    ) {
      id
      sender_user_id
      body
      read_at
      created_at
    }
  }
`)

/**
 * Ungelesene Nachrichten an die angemeldete Person, für die Zahl an der Navigation und
 * in der Chatliste der Hebamme. Die Zeilenrechte beschränken das auf eigene Gespräche.
 */
export const UNREAD_CHAT_MESSAGES = graphql(`
  subscription UnreadChatMessages($userId: uuid!) {
    chat_messages(where: { sender_user_id: { _neq: $userId }, read_at: { _is_null: true } }) {
      id
      patient_user_id
      midwife_user_id
    }
  }
`)

/** Die jeweils letzte Nachricht pro Frau, für die Chatliste der Hebamme. */
export const LATEST_CHAT_MESSAGES = graphql(`
  subscription LatestChatMessages {
    chat_messages(
      distinct_on: patient_user_id
      order_by: [{ patient_user_id: asc }, { created_at: desc }]
    ) {
      id
      patient_user_id
      sender_user_id
      body
      read_at
      created_at
    }
  }
`)

export const SEND_CHAT_MESSAGE_AS_WOMAN = graphql(`
  mutation SendChatMessageAsWoman($body: String!) {
    insert_chat_messages_one(object: { body: $body }) {
      id
    }
  }
`)

export const SEND_CHAT_MESSAGE_AS_MIDWIFE = graphql(`
  mutation SendChatMessageAsMidwife($patientUserId: uuid!, $body: String!) {
    insert_chat_messages_one(object: { patient_user_id: $patientUserId, body: $body }) {
      id
    }
  }
`)

export const MARK_CHAT_MESSAGES_READ = graphql(`
  mutation MarkChatMessagesRead($ids: [uuid!]!, $readAt: timestamptz!) {
    update_chat_messages(where: { id: { _in: $ids } }, _set: { read_at: $readAt }) {
      returning {
        id
        read_at
      }
    }
  }
`)

/** Die eigene Profilzeile der Frau: Mit wem darf sie schreiben? */
export const GET_CHAT_CONNECTION = graphql(`
  query GetChatConnection($userId: uuid!) {
    user_profiles(where: { user_id: { _eq: $userId } }, limit: 1) {
      id
      connected_midwife_id
    }
  }
`)

/**
 * Name der Gegenseite. Beide Rollen dürfen das Konto der verbundenen Person lesen, die
 * Rolle `user` aber nur `id` und `metadata` - deshalb kein `displayName` hier.
 */
export const GET_CHAT_PARTNER = graphql(`
  query GetChatPartner($userId: uuid!) {
    users(where: { id: { _eq: $userId } }) {
      id
      metadata
    }
  }
`)

/** Die verbundenen Frauen für die Chatliste der Hebamme (Filter über die Zeilenrechte). */
export const GET_CHAT_PATIENTS = graphql(`
  query GetChatPatients {
    user_profiles {
      id
      user_id
      user {
        id
        displayName
        metadata
      }
    }
  }
`)

/** Die letzte Nachricht im Gespräch der Frau mit ihrer Hebamme, für ihre Chatliste. */
export const LATEST_CHAT_MESSAGE_WITH_MIDWIFE = graphql(`
  subscription LatestChatMessageWithMidwife($midwifeUserId: uuid!) {
    chat_messages(
      where: { midwife_user_id: { _eq: $midwifeUserId } }
      order_by: { created_at: desc }
      limit: 1
    ) {
      id
      sender_user_id
      body
      created_at
    }
  }
`)

/*
 * Feedback-Chat zwischen den Mitgliedern - Hebammen und Frauen - und dem Institut für
 * Stillberatung (Rolle `me`, Tabelle `feedback_messages`, angelegt mit
 * scripts/add-feedback-messages.mjs und geöffnet mit scripts/open-feedback-to-women.mjs).
 * Pro Mitglied ein Gespräch. Das Institut schreibt aus einem gemeinsamen Postfach, deshalb
 * zählt `is_from_member` und nicht die Absender-Id; beides setzt der Server.
 */

export const FEEDBACK_MESSAGES = graphql(`
  subscription FeedbackMessages($memberUserId: uuid!) {
    feedback_messages(
      where: { member_user_id: { _eq: $memberUserId } }
      order_by: { created_at: desc }
      limit: 300
    ) {
      id
      is_from_member
      body
      read_at
      created_at
    }
  }
`)

/** Ungelesenes der jeweils anderen Seite: das Mitglied fragt `false`, das Institut `true`. */
export const UNREAD_FEEDBACK_MESSAGES = graphql(`
  subscription UnreadFeedbackMessages($fromMember: Boolean!) {
    feedback_messages(
      where: { is_from_member: { _eq: $fromMember }, read_at: { _is_null: true } }
    ) {
      id
      member_user_id
    }
  }
`)

/**
 * Die jeweils letzte Nachricht pro Mitglied. Das Institut bekommt eine Zeile pro
 * Gespräch, Hebamme und Frau über die Zeilenrechte nur die eigene.
 */
export const LATEST_FEEDBACK_MESSAGES = graphql(`
  subscription LatestFeedbackMessages {
    feedback_messages(
      distinct_on: member_user_id
      order_by: [{ member_user_id: asc }, { created_at: desc }]
    ) {
      id
      member_user_id
      is_from_member
      body
      created_at
    }
  }
`)

/** Hebamme und Frau schicken nur den Text; das Gespräch setzt der Server aus dem Token. */
export const SEND_FEEDBACK_AS_MEMBER = graphql(`
  mutation SendFeedbackAsMember($body: String!) {
    insert_feedback_messages_one(object: { body: $body }) {
      id
    }
  }
`)

export const SEND_FEEDBACK_AS_INSTITUTE = graphql(`
  mutation SendFeedbackAsInstitute($memberUserId: uuid!, $body: String!) {
    insert_feedback_messages_one(object: { member_user_id: $memberUserId, body: $body }) {
      id
    }
  }
`)

export const MARK_FEEDBACK_MESSAGES_READ = graphql(`
  mutation MarkFeedbackMessagesRead($ids: [uuid!]!, $readAt: timestamptz!) {
    update_feedback_messages(where: { id: { _in: $ids } }, _set: { read_at: $readAt }) {
      returning {
        id
        read_at
      }
    }
  }
`)

/**
 * Wen das Institut in seiner Liste sieht (nur Rolle `me`): alle Hebammen, damit es auch
 * selbst ein Gespräch beginnen kann, und dazu jede Frau, die schon geschrieben hat.
 */
export const GET_FEEDBACK_MEMBERS = graphql(`
  query GetFeedbackMembers($womenIds: [uuid!]!) {
    users(where: { _or: [{ defaultRole: { _eq: "midwife" } }, { id: { _in: $womenIds } }] }) {
      id
      displayName
      email
      defaultRole
      metadata
    }
  }
`)

export const GET_FEEDBACK_MEMBER = graphql(`
  query GetFeedbackMember($userId: uuid!) {
    users(where: { id: { _eq: $userId } }) {
      id
      displayName
      email
      defaultRole
      metadata
    }
  }
`)
